/**
 * GUS Weekly recipients: who gets the Thursday email.
 * Any admin can read the list; only super admins can change it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as RemoveIcon } from '@mui/icons-material';
import {
  gusWeeklyAPI,
  GusWeeklyRecipient,
  GusWeeklyRecipientList,
  GusWeeklyStatus,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import AddRecipientDialog from './gusWeekly/AddRecipientDialog';
import { focusRing, squarePaper } from './gusWeekly/styles';
import { errorMessage, safeHttpsUrl, statusText } from './gusWeekly/format';

const linkLabel = (r: GusWeeklyRecipient) =>
  r.linked === 'portal_admin'
    ? 'Portal admin'
    : r.linked === 'tenant_user'
    ? `Tenant user${r.project_name ? ` · ${r.project_name}` : ''}`
    : null;

const GusWeeklyRecipients: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const canEdit = user?.is_super_admin === true;

  const [data, setData] = useState<GusWeeklyRecipientList | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<GusWeeklyStatus | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [statusFailed, setStatusFailed] = useState(false);
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<GusWeeklyRecipient | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await gusWeeklyAPI.list());
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load the recipient list'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    gusWeeklyAPI
      .status()
      .then((s) => setStatus(s))
      .catch(() => setStatusFailed(true))
      .finally(() => setStatusLoaded(true));
  }, [load]);

  const mutate = (fn: (rows: GusWeeklyRecipient[]) => GusWeeklyRecipient[]) =>
    setData((d) => {
      if (!d) return d;
      const recipients = fn(d.recipients);
      return { ...d, recipients, active_count: recipients.filter((r) => r.active).length };
    });

  const toggle = async (r: GusWeeklyRecipient) => {
    setSaving((s) => new Set(s).add(r.id));
    try {
      const updated = await gusWeeklyAPI.update(r.id, { active: !r.active });
      mutate((rows) => rows.map((x) => (x.id === r.id ? updated : x)));
      const who = updated.name || updated.email;
      showNotification(
        updated.active ? `${who} will get GUS Weekly` : `${who} won't get GUS Weekly`,
        'success'
      );
    } catch (err) {
      showNotification(errorMessage(err, 'Could not update this recipient'), 'error');
    } finally {
      setSaving((s) => {
        const n = new Set(s);
        n.delete(r.id);
        return n;
      });
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      await gusWeeklyAPI.remove(removing.id);
      mutate((rows) => rows.filter((x) => x.id !== removing.id));
      showNotification(`Removed ${removing.email}`, 'success');
      setRemoving(null);
    } catch (err) {
      showNotification(errorMessage(err, 'Could not remove this recipient'), 'error');
    } finally {
      setRemoveBusy(false);
    }
  };

  const hubUrl = status && status.status !== 'failed' ? safeHttpsUrl(status.help_hub_url) : null;
  const rows = data?.recipients ?? [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            GUS Weekly
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            {!statusLoaded ? (
              <Skeleton variant="text" width={260} />
            ) : statusFailed ? (
              'Last issue status unavailable'
            ) : (
              <>
                <span>{statusText(status)}</span>
                {hubUrl && (
                  <>
                    <span>{' · '}</span>
                    <Link
                      href={hubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={focusRing}
                    >
                      View in the Help Hub
                    </Link>
                  </>
                )}
              </>
            )}
          </Typography>
          {data && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {`${data.active_count} active of ${data.recipients.length} · up to ${data.max_active}`}
            </Typography>
          )}
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            disabled={loading || !!loadError}
            sx={{ borderRadius: 0, ...focusRing }}
          >
            Add recipient
          </Button>
        )}
      </Box>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
          Only super admins can change the GUS Weekly list.
        </Alert>
      )}

      {loadError ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 0 }}
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ borderRadius: 0, ...focusRing }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : loading ? (
        <Box aria-busy="true" aria-label="Loading recipients">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 0 }} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 0 }}>
          <Typography color="text.secondary">
            No recipients — GUS Weekly won't send until someone is added
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>{canEdit ? 'Send' : 'Status'}</TableCell>
                {canEdit && <TableCell align="right">Remove</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name || '—'}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{linkLabel(r) ?? '—'}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Switch
                        checked={r.active}
                        disabled={saving.has(r.id)}
                        onChange={() => toggle(r)}
                        inputProps={{ role: 'switch', 'aria-label': `Send GUS Weekly to ${r.email}` }}
                        sx={{ '& .Mui-focusVisible': focusRing['&.Mui-focusVisible, &:focus-visible'] }}
                      />
                    ) : (
                      <Typography variant="body2">{r.active ? 'On' : 'Off'}</Typography>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <Tooltip title="Remove" componentsProps={{ tooltip: { sx: { borderRadius: 0 } } }}>
                        <span>
                          <IconButton
                            aria-label={`Remove ${r.email}`}
                            onClick={() => setRemoving(r)}
                            disabled={saving.has(r.id)}
                            sx={{ borderRadius: 0, ...focusRing }}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddRecipientDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(rcp) => {
          mutate((rows) => [...rows, rcp]);
          setAdding(false);
          showNotification(`Added ${rcp.email}`, 'success');
        }}
      />

      <Dialog
        open={!!removing}
        onClose={removeBusy ? undefined : () => setRemoving(null)}
        PaperProps={squarePaper}
      >
        <DialogTitle>{`Remove ${removing?.name || removing?.email} from GUS Weekly?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            They will stop receiving the Thursday email. You can add them again later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={removeBusy} sx={{ borderRadius: 0, ...focusRing }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRemove}
            disabled={removeBusy}
            sx={{ borderRadius: 0, ...focusRing }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GusWeeklyRecipients;
